import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import {
  Plus, X, Save, FolderOpen, Clipboard,
  Zap, Check, AlertCircle,
  ArrowRight, Info, Trash2, Search, Loader2
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useFileStore } from "@/stores/files.store";
import { renameService } from "@/services/rename.service";
import { webAdbService } from "@/services/webadb.service";
import { fileSystemService } from "@/services/filesystem.service";
import { RenameMode, RenameRow } from "@/types/rename.types";
import { useLogStore } from "@/stores/log.store";
import { useHistoryStore, OperationType, OperationStatus } from "@/stores/history.store";
import { useDeviceStore } from "@/stores/device.store";
import { useRenameStore } from "@/stores/rename.store";

interface BatchRenameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const BatchRenameDialog: React.FC<BatchRenameDialogProps> = ({ open, onOpenChange }) => {
  const leftState = useFileStore(state => state.left);
  const { setFiles } = useFileStore();
  const { addLog } = useLogStore();
  const { addRecord } = useHistoryStore();
  const { adb } = useDeviceStore();
  const { savedTemplates, saveTemplate } = useRenameStore();
  
  const [mode, setMode] = useState<RenameMode>(RenameMode.TEMPLATE);
  const [template, setTemplate] = useState('{date}_{model}_{customName}');
  const selectedPattern = renameService.PREDEFINED_PATTERNS[0];
  const [customVars, setCustomVars] = useState<{name: string, value: string}[]>([]);
  const [rows, setRows] = useState<RenameRow[]>([]);
  const [regexPattern, setRegexPattern] = useState('');
  const [regexReplace, setRegexReplace] = useState('');
  const [regexError, setRegexError] = useState<string | null>(null);

  const validCount = rows.filter(r => r.isValid).length;
  
  const [newVarName, setNewVarName] = useState('');
  const [newVarValue, setNewVarValue] = useState('');

  // Initialize rows when dialog opens or selection changes
  useEffect(() => {
    if (open) {
      const selectedFiles = leftState.files.filter(f => leftState.selectedPaths.includes(f.path));
      const initialRows: RenameRow[] = selectedFiles.map(file => {
        const extracted = renameService.extractVariables(file.name, selectedPattern);
        return {
          id: crypto.randomUUID(),
          sourceFile: file,
          customInput: '',
          extractedVars: extracted,
          previewName: '',
          isValid: false
        };
      });
      setRows(initialRows);
    }
  }, [open, leftState.selectedPaths, leftState.files, selectedPattern]);

  // Validate regex when pattern changes
  useEffect(() => {
    if (mode === RenameMode.REGEX && regexPattern) {
      const validation = renameService.validateRegex(regexPattern);
      setRegexError(validation.isValid ? null : validation.error || '无效正则');
    } else {
      setRegexError(null);
    }
  }, [regexPattern, mode]);

  // Update previews whenever inputs change
  useEffect(() => {
    const existingNames = new Set(leftState.files.map(f => f.name));
    const newNamesInBatch = new Set<string>();

    const updatedRows = rows.map(row => {
      let newName = '';
      let rowError: string | undefined;

      if (mode === RenameMode.DIRECT) {
        newName = row.customInput;
      } else if (mode === RenameMode.REGEX) {
        if (regexPattern && !regexError) {
          const result = renameService.applyRegexReplace(row.sourceFile.name, regexPattern, regexReplace);
          newName = result.result;
          rowError = result.error;
        } else {
          newName = row.sourceFile.name;
        }
      } else {
        const allVars = {
          ...row.extractedVars,
          ...customVars.reduce((acc, v) => ({ ...acc, [v.name]: v.value }), {}),
          customName: row.customInput
        };
        newName = renameService.renderTemplate(template, allVars);
      }

      // Preserve extension for non-regex modes
      if (mode !== RenameMode.REGEX) {
        const ext = row.sourceFile.name.includes('.') ? row.sourceFile.name.split('.').pop() : '';
        if (ext && !newName.endsWith(`.${ext}`)) {
          newName = `${newName}.${ext}`;
        }
      }

      const validation = renameService.validateFileName(newName);
      let isValid = validation.isValid && !!newName && newName !== row.sourceFile.name;
      let error = rowError || validation.error;

      // Conflict detection
      if (isValid) {
        if (existingNames.has(newName) && newName !== row.sourceFile.name) {
          isValid = false;
          error = '文件名已存在';
        } else if (newNamesInBatch.has(newName)) {
          isValid = false;
          error = '批处理中存在重复名称';
        } else {
          newNamesInBatch.add(newName);
        }
      }

      return {
        ...row,
        previewName: newName,
        isValid,
        error
      };
    });

    // Only update if changed to avoid infinite loop
    const currentPreviews = rows.map(r => r.previewName + r.isValid + r.error).join('|');
    const nextPreviews = updatedRows.map(r => r.previewName + r.isValid + r.error).join('|');
    if (currentPreviews !== nextPreviews) {
      setRows(updatedRows);
    }
  }, [rows, mode, template, customVars, leftState.files, regexPattern, regexReplace, regexError]);

  const addCustomVar = () => {
    if (newVarName && newVarValue) {
      if (customVars.some(v => v.name === newVarName)) {
        addLog('warning', `变量名 ${newVarName} 已存在`);
        return;
      }
      setCustomVars([...customVars, { name: newVarName, value: newVarValue }]);
      setNewVarName('');
      setNewVarValue('');
    }
  };

  const removeCustomVar = (name: string) => {
    setCustomVars(customVars.filter(v => v.name !== name));
  };

  const [isExecuting, setIsExecuting] = useState(false);
  const [executeProgress, setExecuteProgress] = useState(0);

  const handleExecute = async () => {
    const isAdb = leftState.path.startsWith('adb://');
    if (isAdb && !adb) {
      addLog('error', '执行重命名失败：设备未连接');
      return;
    }

    const validRows = rows.filter(r => r.isValid);
    if (validRows.length === 0) return;

    setIsExecuting(true);
    setExecuteProgress(0);
    addLog('info', `开始执行 ${validRows.length} 个重命名操作...`);

    const records: any[] = [];
    let successCount = 0;
    let failCount = 0;
    let completed = 0;

    const CONCURRENCY = navigator.hardwareConcurrency || 4;
    const active = new Set<Promise<void>>();

    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      const idx = i;

      const task = (async () => {
        const parentPath = row.sourceFile.path.includes('/')
          ? row.sourceFile.path.substring(0, row.sourceFile.path.lastIndexOf('/') + 1)
          : '';
        const targetPath = parentPath + row.previewName;

        try {
          if (isAdb) {
            await renameService.renameOnDevice(adb!, row.sourceFile.path, targetPath);
          } else if (row.sourceFile.handle && leftState.directoryHandle) {
            await renameService.renameLocal(row.sourceFile.handle, leftState.directoryHandle, row.previewName);
          } else {
            throw new Error('缺少句柄');
          }

          records.push({
            id: crypto.randomUUID(),
            type: OperationType.RENAME,
            timestamp: new Date(),
            status: OperationStatus.COMPLETED,
            details: {
              sourcePath: row.sourceFile.path,
              targetPath: targetPath,
              oldName: row.sourceFile.name,
              newName: row.previewName
            }
          });
          successCount++;
          addLog('success', `[${idx + 1}/${validRows.length}] ${row.sourceFile.name} -> ${row.previewName}`);
        } catch (e: any) {
          failCount++;
          addLog('error', `[${idx + 1}/${validRows.length}] 失败: ${row.sourceFile.name} - ${e.message}`);
        }

        completed++;
        setExecuteProgress((completed / validRows.length) * 100);
      })().finally(() => active.delete(task));

      active.add(task);
      if (active.size >= CONCURRENCY) {
        await Promise.race(active);
      }
    }
    await Promise.all(active);

    // Record history
    if (records.length > 0) {
      if (records.length === 1) {
        addRecord(records[0]);
      } else {
        addRecord({
          id: crypto.randomUUID(),
          type: OperationType.BATCH_RENAME,
          timestamp: new Date(),
          status: failCount > 0 ? OperationStatus.FAILED : OperationStatus.COMPLETED,
          details: { sourcePath: 'multiple' },
          children: records
        });
      }
    }

    // Summary
    if (failCount === 0) {
      addLog('success', `重命名完成: 全部 ${successCount} 个文件成功`);
    } else {
      addLog('warning', `重命名完成: ${successCount} 成功, ${failCount} 失败`);
    }

    // Refresh file list
    try {
      if (isAdb) {
        const adbPath = leftState.path.replace('adb://', '');
        const updatedFiles = await webAdbService.findFiles(adb!, adbPath, ['*']);
        setFiles('left', updatedFiles);
      } else if (leftState.directoryHandle) {
        const updatedFiles = await fileSystemService.readDirectory(leftState.directoryHandle);
        setFiles('left', updatedFiles);
      }
    } catch { /* ignore refresh error */ }

    setIsExecuting(false);
    if (failCount === 0) {
      onOpenChange(false);
    }
  };

  const importFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
      if (lines.length === 0) return;

      const newRows = [...rows];
      lines.forEach((line, i) => {
        if (newRows[i]) {
          newRows[i].customInput = line;
        }
      });
      setRows(newRows);
      addLog('info', `从剪贴板导入了 ${Math.min(lines.length, rows.length)} 行内容`);
    } catch {
      addLog('error', '无法读取剪贴板');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Zap className="w-6 h-6 text-yellow-500 fill-yellow-500/20" />
            批量重命名
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-hidden flex flex-col px-6 pb-6 gap-4">
          {/* Mode Selection */}
          <div className="p-3 bg-muted/30 rounded-lg border flex items-center justify-between">
            <RadioGroup
              value={mode}
              onValueChange={(v) => setMode(v as RenameMode)}
              className="flex gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value={RenameMode.TEMPLATE} id="mode-template" />
                <Label htmlFor="mode-template" className="font-medium cursor-pointer">模板模式</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value={RenameMode.REGEX} id="mode-regex" />
                <Label htmlFor="mode-regex" className="font-medium cursor-pointer">正则替换</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value={RenameMode.DIRECT} id="mode-direct" />
                <Label htmlFor="mode-direct" className="font-medium cursor-pointer">直接重命名</Label>
              </div>
            </RadioGroup>
            <div className="text-[11px] text-muted-foreground flex items-center gap-1 bg-background px-2 py-1 rounded border shadow-sm">
              <Info className="w-3 h-3 text-primary" />
              {mode === RenameMode.TEMPLATE && '使用模板组合提取的变量'}
              {mode === RenameMode.REGEX && '使用正则表达式批量替换'}
              {mode === RenameMode.DIRECT && '直接输入新文件名'}
            </div>
          </div>
          
          {/* Configuration Area */}
          {mode === RenameMode.TEMPLATE && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Custom Variables */}
              <div className="p-3 border rounded-lg space-y-3 bg-card shadow-sm">
                <Label className="text-[10px] font-bold uppercase text-muted-foreground tracking-wider">自定义变量 (固定内容)</Label>
                <div className="flex gap-2">
                  <Input 
                    placeholder="变量名" 
                    className="h-8 text-xs" 
                    value={newVarName}
                    onChange={e => setNewVarName(e.target.value)}
                  />
                  <Input 
                    placeholder="值" 
                    className="h-8 text-xs" 
                    value={newVarValue}
                    onChange={e => setNewVarValue(e.target.value)}
                  />
                  <Button size="icon" variant="secondary" className="h-8 w-8 shrink-0" onClick={addCustomVar}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1.5 min-h-[24px]">
                  {customVars.map(v => (
                    <Badge 
                      key={v.name} 
                      variant="secondary"
                      className="gap-1 pr-1 text-[10px] h-6 bg-green-500/10 text-green-700 border-green-200"
                    >
                      {v.name}: {v.value}
                      <button 
                        onClick={() => removeCustomVar(v.name)}
                        className="hover:text-destructive transition-colors ml-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
              
              {/* Template Editor */}
              <div className="p-3 border rounded-lg space-y-3 bg-card shadow-sm">
                <div className="flex items-center justify-between">
                  <Label className="text-[10px] font-bold uppercase text-muted-foreground tracking-wider">命名模板</Label>
                  <div className="flex gap-1">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] gap-1">
                          <FolderOpen className="w-3 h-3" /> 我的模板
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        {savedTemplates.map(t => (
                          <DropdownMenuItem key={t.id} onClick={() => setTemplate(t.template)}>
                            <div className="flex flex-col gap-0.5">
                              <span className="text-xs font-medium">{t.name}</span>
                              <span className="text-[10px] text-muted-foreground truncate">{t.template}</span>
                            </div>
                          </DropdownMenuItem>
                        ))}
                        {savedTemplates.length === 0 && <DropdownMenuItem disabled>暂无保存模板</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 px-2 text-[10px] gap-1"
                      onClick={() => {
                        const name = prompt('请输入模板名称');
                        if (name) {
                          saveTemplate({ name, template });
                        }
                      }}
                    >
                      <Save className="w-3 h-3" /> 保存
                    </Button>
                  </div>
                </div>
                <Input 
                  value={template}
                  onChange={e => setTemplate(e.target.value)}
                  placeholder="{date}_{model}_{customName}"
                  className="h-8 text-sm font-mono bg-muted/20"
                />
                <div className="flex flex-wrap gap-1">
                  {['date', 'time', 'model', 'sequence', 'action'].map(v => (
                    <Badge 
                      key={v} 
                      variant="outline" 
                      className="text-[10px] h-5 cursor-pointer hover:bg-primary/10 transition-colors"
                      onClick={() => setTemplate(prev => prev + `{${v}}`)}
                    >
                      {`{${v}}`}
                    </Badge>
                  ))}
                  {customVars.map(v => (
                    <Badge 
                      key={v.name} 
                      variant="outline" 
                      className="text-[10px] h-5 cursor-pointer border-green-500/50 text-green-600 hover:bg-green-50"
                      onClick={() => setTemplate(prev => prev + `{${v.name}}`)}
                    >
                      {`{${v.name}}`}
                    </Badge>
                  ))}
                  <Badge 
                    variant="outline" 
                    className="text-[10px] h-5 cursor-pointer border-blue-500/50 text-blue-600 hover:bg-blue-50"
                    onClick={() => setTemplate(prev => prev + '{customName}')}
                  >
                    {'{customName}'}
                  </Badge>
                </div>
              </div>
            </div>
          )}

          {/* Regex Configuration */}
          {mode === RenameMode.REGEX && (
            <div className="p-3 border rounded-lg space-y-3 bg-card shadow-sm">
              <Label className="text-[10px] font-bold uppercase text-muted-foreground tracking-wider">正则表达式配置</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">匹配模式</Label>
                  <Input
                    value={regexPattern}
                    onChange={e => setRegexPattern(e.target.value)}
                    placeholder="例: (\d{8})_(.+)"
                    className={cn("h-8 text-xs font-mono", regexError && "border-destructive")}
                  />
                  {regexError && <p className="text-[10px] text-destructive">{regexError}</p>}
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">替换为</Label>
                  <Input
                    value={regexReplace}
                    onChange={e => setRegexReplace(e.target.value)}
                    placeholder="例: $2_$1"
                    className="h-8 text-xs font-mono"
                  />
                  <p className="text-[10px] text-muted-foreground">使用 $1, $2... 引用捕获组</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Main Editor Table */}
          <div className="flex-1 min-h-0 border rounded-lg overflow-hidden flex flex-col bg-card shadow-inner">
            <div className={cn(
              "gap-4 px-4 py-2 bg-muted/50 border-b text-[10px] font-bold uppercase text-muted-foreground tracking-widest",
              mode === RenameMode.REGEX ? "grid grid-cols-[40px_1fr_1fr]" : "grid grid-cols-[40px_1.5fr_1.5fr_1.5fr]"
            )}>
              <span>#</span>
              <span>源文件名</span>
              {mode !== RenameMode.REGEX && (
                <span>{mode === RenameMode.DIRECT ? '新文件名' : '自定义内容'}</span>
              )}
              <span>预览结果</span>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              <div className="divide-y">
                {rows.map((row, index) => (
                  <div key={row.id} className={cn(
                    "gap-4 px-4 py-3 items-center hover:bg-muted/10 transition-colors group",
                    mode === RenameMode.REGEX ? "grid grid-cols-[40px_1fr_1fr]" : "grid grid-cols-[40px_1.5fr_1.5fr_1.5fr]"
                  )}>
                    <span className="text-xs text-muted-foreground tabular-nums font-mono">{index + 1}</span>
                    
                    <div className="min-w-0">
                      <p className="text-xs font-medium truncate group-hover:text-clip group-hover:whitespace-normal break-all" title={row.sourceFile.path}>
                        {row.sourceFile.name}
                      </p>
                      {mode === RenameMode.TEMPLATE && Object.keys(row.extractedVars).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1 opacity-60 group-hover:opacity-100 transition-opacity">
                          {Object.entries(row.extractedVars).slice(0, 3).map(([k, v]) => (
                            <span key={k} className="text-[9px] bg-muted px-1 rounded text-muted-foreground border border-muted-foreground/10">
                              {k}:{v}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {mode !== RenameMode.REGEX && (
                      <Input
                        value={row.customInput}
                        onChange={e => {
                          const newRows = [...rows];
                          newRows[index].customInput = e.target.value;
                          setRows(newRows);
                        }}
                        placeholder={mode === RenameMode.DIRECT ? "输入新名称" : "输入内容"}
                        className="h-8 text-xs bg-background/50 focus:bg-background"
                      />
                    )}
                    
                    <div className="flex items-center gap-2 min-w-0">
                      <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0" />
                      <div className="min-w-0 flex flex-col">
                        <span className={cn(
                          "text-xs truncate font-mono font-medium",
                          row.isValid ? "text-green-600" : row.previewName === row.sourceFile.name ? "text-muted-foreground" : "text-destructive"
                        )}>
                          {row.previewName || '(未生成)'}
                        </span>
                        {!row.isValid && row.error && (
                          <span className="text-[9px] text-destructive flex items-center gap-0.5 mt-0.5">
                            <AlertCircle className="w-2.5 h-2.5" /> {row.error}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {rows.length === 0 && (
                  <div className="py-20 text-center text-muted-foreground flex flex-col items-center gap-3">
                    <Search className="w-12 h-12 opacity-10" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium">未选择任何文件</p>
                      <p className="text-xs opacity-70">请先在主界面左侧面板选择需要重命名的文件</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="p-2 bg-muted/30 border-t flex items-center justify-between px-4">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1 bg-background" onClick={importFromClipboard}>
                  <Clipboard className="w-3 h-3" /> 剪贴板导入
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1 bg-background" onClick={() => {
                  setRows(rows.map(r => {
                    const parts = [];
                    if (r.extractedVars.environment) parts.push(r.extractedVars.environment);
                    if (r.extractedVars.action) parts.push(r.extractedVars.action);
                    return { ...r, customInput: parts.join('_') };
                  }));
                }}>
                  <Zap className="w-3 h-3" /> 智能填充
                </Button>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-muted-foreground">
                  有效项目: <span className="text-green-600 font-bold">{validCount}</span> / {rows.length}
                </span>
                <Separator orientation="vertical" className="h-3" />
                <Button variant="ghost" size="sm" className="h-6 text-[10px] text-destructive hover:bg-destructive/10" onClick={() => setRows(rows.map(r => ({ ...r, customInput: '' })))}>
                  <Trash2 className="w-3 h-3 mr-1" /> 清空输入
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        <DialogFooter className="p-6 pt-2 border-t bg-muted/10">
          {isExecuting && (
            <div className="flex-1 flex items-center gap-3 mr-4">
              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${executeProgress}%` }} />
              </div>
              <span className="text-xs text-muted-foreground tabular-nums">{Math.round(executeProgress)}%</span>
            </div>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isExecuting}>取消</Button>
          <Button
            disabled={validCount === 0 || isExecuting}
            onClick={handleExecute}
            className="gap-2 min-w-[140px] shadow-lg shadow-primary/20"
          >
            {isExecuting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                执行中...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                执行重命名 ({validCount})
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
